import {
	Button,
	CycleIcon,
	EmptyState,
	SpinnerWithBackground,
	UnsavedChangesDialog,
	useUnsavedChangesWarning,
} from '@viapeople/components'
import {
	applyEdgeChanges,
	applyNodeChanges,
	Background,
	type Connection,
	Controls,
	type EdgeTypes,
	type NodeTypes,
	type OnConnectStart,
	type OnEdgesChange,
	type OnNodesChange,
	ReactFlow,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { v7 as uuidv7 } from 'uuid'
import { useToast } from '../../../../utils/ToastContext'
import { trpc } from '../../../../utils/trpc'
import { useCycleStore } from '../../store'
import { ManageCycleStep } from '../cycle-builder/ManageCycleStep'
import { StepInfo } from '../cycle-builder/StepInfo'
import { CustomEdgeComponent, CustomNodeComponent, FixedNodeComponent, StepsPaletteCard } from './components'
import { useCycleStepsStore } from './store/useCycleStepsStore'
import { type CustomEdge, type CustomNode, StepType } from './types'
import { formatStepType, stepMetadata, stepTypes } from './utils'

interface ExtendedConnection extends Connection {
	data?: {
		label?: string
		movesForward?: boolean
	}
}

export const CycleStepsTab = () => {
	const { organizationId, cycleId } = useParams<{ organizationId: string; cycleId: string }>()
	const cycle = useCycleStore((state) => state.cycle)
	const { showToast } = useToast()
	const [isManageStepModalOpen, setIsManageStepModalOpen] = useState(false)
	const queryClient = useQueryClient()
	const [dragSourceInfo, setDragSourceInfo] = useState<{
		nodeId: string
		handleId: string
		stepType: StepType
	} | null>(null)
	const isInitializedRef = useRef(false)

	const {
		nodes,
		edges,
		hasChanges,
		selectedNode,
		editingNode,
		setNodes,
		setEdges,
		addNode,
		deleteNode,
		deleteEdge,
		connectNodes: connectNodesInStore,
		setHasChanges,
		selectNode,
		editNode,
		resetChanges,
		setDiagramDefinition,
	} = useCycleStepsStore()

	const { data: stepRequirements } = useQuery({
		...trpc.admin.cycles.getGeneralConfigurations.queryOptions({
			organizationId: organizationId as string,
			cycleId: cycleId as string,
		}),
		refetchOnWindowFocus: true,
		refetchOnMount: true,
	})

	const { data: cycleStepsDiagram, isLoading } = useQuery({
		...trpc.admin.cycles.steps.diagram.show.queryOptions({
			organizationId: organizationId as string,
			cycleId: cycleId as string,
		}),
		enabled: !!organizationId && !!cycleId,
	})

	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(hasChanges && !isLoading)

	const { mutate: updateDiagram, isPending: isSaving } = useMutation({
		...trpc.admin.cycles.steps.diagram.edit.mutationOptions({
			onSuccess: () => {
				showToast('Cycle steps diagram saved successfully', 'info')
				setHasChanges(false)
			},
			onError: (error) => {
				showToast(`Error saving cycle steps diagram: ${error.message}`, 'error')
			},
			onSettled: () => {
				queryClient.invalidateQueries({
					queryKey: trpc.admin.cycles.steps.diagram.show.queryKey({
						organizationId: organizationId as string,
						cycleId: cycleId as string,
					}),
					exact: false,
					refetchType: 'all',
				})
			},
		}),
	})

	const handleEditNode = useCallback(
		(nodeId: string) => {
			editNode(nodeId)
		},
		[editNode],
	)

	const handleSelectNode = useCallback(
		(nodeId: string) => {
			selectNode(nodeId)
		},
		[selectNode],
	)

	const handleDeleteNode = useCallback(
		(nodeId: string) => {
			const nodeToDelete = nodes.find((node) => node.id === nodeId)
			if (nodeToDelete?.data.isFixed) {
				return
			}
			deleteNode(nodeId)
		},
		[nodes, deleteNode],
	)

	const handleDeleteEdge = useCallback(
		(edgeId: string) => {
			deleteEdge(edgeId)
		},
		[deleteEdge],
	)

	const initializeFixedNodes = useCallback(
		(
			existingNodes: Array<{
				id: string
				data: { stepType?: StepType; label?: string }
				position: { x: number; y: number }
			}> = [],
		) => {
			const existingStartNode = existingNodes.find((node) => node.data.stepType === StepType.CYCLE_START)

			const existingEndNode = existingNodes.find((node) => node.data.stepType === StepType.CYCLE_END)

			const startNode = {
				id: existingStartNode ? existingStartNode.id : uuidv7(),
				data: {
					label: 'Cycle Start',
					stepType: StepType.CYCLE_START,
					onEdit: handleEditNode,
					isFixed: true,
				},
				position: existingStartNode ? existingStartNode.position : { x: 250, y: 50 },
				type: 'fixedNode',
			}

			const endNode = {
				id: existingEndNode ? existingEndNode.id : uuidv7(),
				data: {
					label: 'Cycle End',
					stepType: StepType.CYCLE_END,
					onEdit: handleEditNode,
					isFixed: true,
				},
				position: existingEndNode ? existingEndNode.position : { x: 250, y: 350 },
				type: 'fixedNode',
			}

			return [startNode, endNode]
		},
		[handleEditNode],
	)

	// Track the last processed diagram to prevent re-processing
	const lastProcessedDiagramRef = useRef<string | null>(null)

	useEffect(() => {
		// Generate a simple key to track if diagram data has changed
		const diagramKey = cycleStepsDiagram?.data
			? JSON.stringify({
					nodesCount: cycleStepsDiagram.data.nodes?.length,
					edgesCount: cycleStepsDiagram.data.edges?.length,
				})
			: null

		// Skip if we've already processed this exact diagram
		if (diagramKey && lastProcessedDiagramRef.current === diagramKey) {
			return
		}

		if (cycleStepsDiagram?.data) {
			// Set the ref FIRST to prevent re-processing during state updates
			lastProcessedDiagramRef.current = diagramKey
			isInitializedRef.current = true

			const { nodes: diagramNodes, edges: diagramEdges } = cycleStepsDiagram.data
			const diagramDefinition = cycleStepsDiagram.cycleStepsDiagramDefinitions

			if (diagramDefinition) {
				setDiagramDefinition(diagramDefinition)
			}

			const fixedNodes = initializeFixedNodes(
				diagramNodes as Array<{
					id: string
					data: { stepType?: StepType; label?: string }
					position: { x: number; y: number }
				}>,
			)

			const loadedNodes = diagramNodes?.filter(
				(node) => node.data.stepType !== StepType.CYCLE_START && node.data.stepType !== StepType.CYCLE_END,
			)

			if (loadedNodes && loadedNodes.length > 0) {
				const formattedNodes = loadedNodes.map((node) => ({
					...node,
					data: {
						label: (node.data as { label: string }).label || 'Unknown Step',
						stepType: (node.data as { stepType?: StepType }).stepType,
						onEdit: handleEditNode,
						onDelete: handleDeleteNode,
						onSelect: handleSelectNode,
						options: node.data.options,
					},
					type: 'customNode',
				})) as CustomNode[]

				setNodes([...fixedNodes, ...formattedNodes], false)
			} else {
				setNodes(fixedNodes, false)
			}

			if (diagramEdges && diagramEdges.length > 0) {
				const formattedEdges = diagramEdges.map((edge) => ({
					...edge,
					type: 'customEdge',
					data: {
						label: edge.label || '',
						allowedNextSteps: edge.allowedNextSteps,
						key: edge.key,
						movesForward: edge.movesForward,
						sourceHandle: edge.sourceHandle,
						targetHandle: edge.targetHandle,
						onDelete: handleDeleteEdge,
					},
				})) as CustomEdge[]

				setEdges(formattedEdges, false)
			}

			resetChanges()
		} else if (!isInitializedRef.current) {
			// Only initialize if not already initialized
			isInitializedRef.current = true
			setNodes(initializeFixedNodes(), false)
			resetChanges()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		cycleStepsDiagram,
		handleDeleteEdge,
		handleDeleteNode,
		handleEditNode,
		handleSelectNode,
		initializeFixedNodes,
		resetChanges,
		setDiagramDefinition,
		setEdges,
		setNodes,
	])

	useEffect(() => {
		if (editingNode) {
			setIsManageStepModalOpen(true)
		}
	}, [editingNode])

	const onConnect = (params: Connection) => {
		let edgeLabel = ''
		const sourceNode = nodes.find((node) => node.id === params.source)

		if (sourceNode?.data?.stepType && params.sourceHandle) {
			const stepType = sourceNode.data.stepType
			edgeLabel = stepMetadata[stepType].labels[params.sourceHandle.replace('source-', '')]
			console.log(edgeLabel, params.sourceHandle, stepType, stepMetadata[stepType].labels)
		}

		const connectionWithLabel: ExtendedConnection = {
			...params,
			data: {
				label: edgeLabel,
				movesForward: !params.sourceHandle?.includes('reject'),
			},
		}

		connectNodesInStore(connectionWithLabel)
	}

	const onConnectStart: OnConnectStart = (_, params) => {
		const { nodeId, handleId, handleType } = params
		if (!nodeId || !handleId) return

		const sourceNode = nodes.find((node) => node.id === nodeId)
		if (!sourceNode || !sourceNode.data.stepType) return

		if (handleType === 'source' && handleId.includes('reject')) {
			setDragSourceInfo({
				nodeId,
				handleId,
				stepType: sourceNode.data.stepType,
			})
		}
	}

	const onConnectEnd = () => {
		setDragSourceInfo(null)
	}

	const addNodeToCanvas = (label: string, stepType: StepType) => {
		addNode(label, stepType)
	}

	const handleSaveDiagram = () => {
		if (!organizationId || !cycleId) return

		// Templates can't launch (buildCycle and launch both reject isTemplate cycles).
		// Skip the rater-group-required check on templates so deal-team admins can save
		// the diagram and attach rater groups afterwards via the integration wizard.
		const isTemplateCycle = cycle?.id === cycleId && cycle?.isTemplate
		if (!isTemplateCycle) {
			const groupEvaluationNodes = nodes.filter((node) => node.data.stepType === StepType.GROUP_EVALUATION)
			const nodesWithoutGroup = groupEvaluationNodes.filter((node) => !node.data.options?.group)

			if (nodesWithoutGroup.length > 0) {
				showToast('Please select a rater group for all Group Evaluation steps before saving', 'error')
				return
			}
		}

		const formattedNodes = nodes.map((node) => ({
			id: node.id,
			position: node.position,
			data: {
				label: node.data.label,
				stepType: node.data.stepType ?? '',
				options: node.data.options,
				isFixed: node.data.isFixed ?? false,
			},
			type: node.type,
		}))

		const formattedEdges = edges.map((edge) => ({
			id: edge.id,
			source: edge.source,
			target: edge.target,
			label: edge.data?.label ?? '',
			key: edge.data?.key,
			movesForward: edge.data?.movesForward,
			allowedNextSteps: edge.data?.allowedNextSteps,
			sourceHandle: edge.data?.sourceHandle,
			targetHandle: edge.data?.targetHandle,
			type: edge.type,
			data: {
				key: edge.data?.key ?? '',
				movesForward: edge.data?.movesForward ?? true,
			},
		}))

		updateDiagram({
			organizationId,
			cycleId,
			nodes: formattedNodes,
			edges: formattedEdges,
		})
	}

	const onNodesChange: OnNodesChange = (changes) => {
		const filteredChanges = changes.filter((change) => {
			if (change.type === 'remove') {
				const nodeToRemove = nodes.find((node) => node.id === change.id)
				return !nodeToRemove?.data.isFixed
			}
			return true
		})

		const updatedNodes = applyNodeChanges(filteredChanges, nodes)
		setNodes(updatedNodes as CustomNode[])
		setHasChanges(true)
	}

	const onEdgesChange: OnEdgesChange = (changes) => {
		const updatedEdges = applyEdgeChanges(changes, edges)
		setEdges(updatedEdges as CustomEdge[])
		setHasChanges(true)
	}

	const nodeTypes = {
		customNode: CustomNodeComponent as NodeTypes['customNode'],
		fixedNode: FixedNodeComponent as NodeTypes['fixedNode'],
	}

	const edgeTypes = {
		customEdge: CustomEdgeComponent as EdgeTypes['customEdge'],
	}

	const handleCloseManageStepModal = () => {
		setIsManageStepModalOpen(false)
		editNode(undefined)
	}

	if (isLoading) {
		return <SpinnerWithBackground />
	}

	if (!organizationId || !cycleId) {
		return <div>Organization or cycle not found</div>
	}

	if (!cycle) return null

	const isReady =
		(cycle.id === cycleId && cycle.isTemplate) ||
		(stepRequirements?.adminsCount &&
			stepRequirements?.participantsCount &&
			stepRequirements?.rolesCount &&
			stepRequirements?.cycleHierarchyFile?.id)

	if (!isReady) {
		return (
			<EmptyState
				title='Cycle steps not available'
				description='You must assign participants, roles and a hierarchy (at the cycle or organization level) before creating cycle steps'
				icon={CycleIcon}
			/>
		)
	}

	return (
		<main className='flex flex-col flex-1 pt-6 gap-4'>
			<div className='flex flex-1'>
				<div data-testid='cycle-steps-palette' className='w-60 mr-2 relative max-h-[calc(100vh-16.3rem)] pr-2'>
					{stepTypes.map((stepType) => (
						<StepsPaletteCard
							key={stepType}
							icon={stepMetadata[stepType].icon}
							displayName={formatStepType(stepType)}
							stepType={stepType}
							onClick={() => addNodeToCanvas(formatStepType(stepType), stepType)}
						/>
					))}
				</div>
				<div
					data-testid='cycle-steps-canvas'
					className='flex-1 overflow-y-auto border border-neutral-200 rounded-md relative'
					style={{ height: 'calc(100vh - 16.3rem)' }}>
					<ReactFlow
						nodes={nodes.map((node) => ({
							...node,
							data: {
								...node.data,
								dragSourceInfo,
							},
							selected: node.id === selectedNode?.id,
						}))}
						edges={edges}
						onNodesChange={onNodesChange}
						onEdgesChange={onEdgesChange}
						onConnect={onConnect}
						onConnectStart={onConnectStart}
						onConnectEnd={onConnectEnd}
						nodeTypes={nodeTypes}
						edgeTypes={edgeTypes}
						onPaneClick={() => selectNode(undefined)}
						onNodeClick={(_, node) => selectNode(node.id)}
						defaultEdgeOptions={{
							type: 'customEdge',
						}}>
						{selectedNode && <StepInfo stepId={selectedNode.id} />}
						<Controls />
						<Background gap={12} size={1} />
					</ReactFlow>
					{hasChanges && (
						<div className='absolute top-4 right-4'>
							<Button
								data-testid='cycle-steps-save-btn'
								variant='primary'
								onClick={handleSaveDiagram}
								isLoading={isSaving}
								disabled={isSaving}>
								Save Changes
							</Button>
						</div>
					)}
				</div>
			</div>
			<UnsavedChangesDialog isBlocked={isBlocked} confirmLeave={confirmLeave} cancelLeave={cancelLeave} />
			{editingNode && (
				<ManageCycleStep
					isOpen={isManageStepModalOpen}
					onClose={handleCloseManageStepModal}
					cycleId={cycleId}
					organizationId={organizationId}
					stepId={editingNode.id}
					stepType={editingNode.data.stepType as StepType}
				/>
			)}
		</main>
	)
}
